import React, { useState, useEffect } from 'react';

const AdminCouponPanel = () => {
  const [coupons, setCoupons] = useState([]);
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState('PERCENTAGE');
  const [discountValue, setDiscountValue] = useState('');
  const [minPurchase, setMinPurchase] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  // 🌟 DYNAMIC HELPER FUNCTION: Safely pulls whichever token key is currently stored in active session memory
  const getActiveToken = () => {
    return localStorage.getItem('modische_token') || localStorage.getItem('modische_auth_token');
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    const token = getActiveToken();
    if (!token) return;

    try {
      const res = await fetch('https://api.modischekledings.com/api/admin/coupons', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setCoupons(data.coupons);
    } catch (err) {
      console.error("Error fetching coupons:", err);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    const token = getActiveToken();

    if (!token) {
      alert("❌ Authentication session missing. Please log out and sign back in.");
      return;
    }

    try {
      const res = await fetch('https://api.modischekledings.com/api/admin/coupons', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          code, 
          discountType, 
          discountValue: Number(discountValue), 
          minPurchaseAmount: Number(minPurchase), 
          expiryDate 
        })
      });
      const data = await res.json();
      
      if (data.success) {
        alert("✨ Promotion coupon registered successfully!");
        fetchCoupons();
        setCode(''); setDiscountValue(''); setMinPurchase(''); setExpiryDate('');
      } else {
        alert(data.message || "Operation rejected by validation filters.");
      }
    } catch (err) {
      alert("Error generating coupon entry.");
    }
  };

  const toggleCouponState = async (id, currentState) => {
    const token = getActiveToken();
    if (!token) return;

    try {
      await fetch(`https://api.modischekledings.com/api/admin/coupons/${id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ isActive: !currentState })
      });
      fetchCoupons();
    } catch (err) {
      console.error("Error changing coupon state flags:", err);
    }
  };

  const deleteCoupon = async (id) => {
    const token = getActiveToken();
    if (!token) return;

    if (window.confirm("Permanently wipe this coupon code?")) {
      try {
        await fetch(`https://api.modischekledings.com/api/admin/coupons/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        fetchCoupons();
      } catch (err) {
        console.error("Error during coupon destruction:", err);
      }
    }
  };

  return (
    <div className="space-y-10 font-sans text-[#2C1E12] p-4">
      <form onSubmit={handleCreateCoupon} className="bg-white border border-[#E5D3B3]/40 p-6 rounded-sm space-y-4 max-w-xl text-left">
        <h3 className="font-serif text-lg uppercase tracking-wide">Generate Campaign Coupon</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[9px] uppercase tracking-widest block font-bold mb-1">Promo Code</label>
            <input type="text" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="E.G. STREET40" className="w-full bg-[#F5F5DC]/40 border border-zinc-200 text-xs p-2.5 uppercase rounded-xs font-mono font-bold" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest block font-bold mb-1">Discount Type</label>
            <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} className="w-full bg-white border border-zinc-200 text-xs p-2.5 rounded-xs">
              <option value="PERCENTAGE">Percentage (%)</option>
              <option value="FLAT">Flat Amount (₹)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-[9px] uppercase tracking-widest block font-bold mb-1">Value</label>
            <input type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} required placeholder="40 or 500" className="w-full bg-[#F5F5DC]/40 border border-zinc-200 text-xs p-2.5 rounded-xs" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest block font-bold mb-1">Min Order (₹)</label>
            <input type="number" value={minPurchase} onChange={(e) => setMinPurchase(e.target.value)} placeholder="0" className="w-full bg-[#F5F5DC]/40 border border-zinc-200 text-xs p-2.5 rounded-xs" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest block font-bold mb-1">Expiry Date</label>
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="w-full bg-[#F5F5DC]/40 border border-zinc-200 text-xs p-2 rounded-xs" />
          </div>
        </div>

        <button type="submit" className="w-full bg-[#2C1E12] text-[#F5F5DC] text-[9px] uppercase tracking-widest py-3 font-bold hover:opacity-95 transition rounded-xs border-none cursor-pointer">
          Deploy Coupon Code
        </button>
      </form>

      <div className="bg-white border border-[#E5D3B3]/40 p-6 rounded-sm overflow-x-auto text-left">
        <h3 className="font-serif text-lg uppercase tracking-wide mb-4">Active Showroom Coupons</h3>
        <table className="w-full text-xs font-mono border-collapse">
          <thead>
            <tr className="bg-[#2C1E12] text-[#F5F5DC] text-[9px] uppercase tracking-wider text-left">
              <th className="p-3">Code</th>
              <th className="p-3">Type</th>
              <th className="p-3">Discount</th>
              <th className="p-3">Min Spend</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {coupons.map((c) => (
              <tr key={c._id} className="hover:bg-zinc-50/60">
                <td className="p-3 font-bold text-[#2C1E12] tracking-wide">{c.code}</td>
                <td className="p-3 text-zinc-500 uppercase text-[10px]">{c.discountType}</td>
                <td className="p-3 font-sans font-bold">{c.discountType === 'PERCENTAGE' ? `${c.discountValue}%` : `₹${c.discountValue}`}</td>
                <td className="p-3 font-sans text-zinc-500">₹{c.minPurchaseAmount}</td>
                <td className="p-3">
                  <span className={`text-[8px] px-2 py-0.5 rounded-xs font-bold ${c.isActive ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                    {c.isActive ? 'ACTIVE' : 'PAUSED'}
                  </span>
                </td>
                <td className="p-3 flex justify-center gap-3">
                  <button onClick={() => toggleCouponState(c._id, c.isActive)} className="text-[9px] uppercase tracking-wider bg-transparent border-none font-bold text-[#664229] hover:underline cursor-pointer">
                    Toggle State
                  </button>
                  <button onClick={() => deleteCoupon(c._id)} className="text-[9px] uppercase tracking-wider bg-transparent border-none font-bold text-red-800 hover:underline cursor-pointer">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCouponPanel;